import {
  Color4, Constants, Matrix, MirrorTexture, Plane, RenderTargetTexture, Texture, ThinEngine, Vector2,
} from "@babylonjs/core";
import type { AbstractMesh, BaseTexture, Camera, Scene, ShaderMaterial } from "@babylonjs/core";
import { CaptureRenderList } from "./capture-culling.ts";

export interface OceanCaptureOptions {
  /** Full-resolution HDR captures are the quality default. */
  resolutionScale?: number;
  /** False skips both captures (the water falls back to sky reflection and bed depth) and shrinks their targets. */
  enabled?: boolean;
  seaLevel?: number;
}

/** Scene-owned, untonemapped planar captures. Water composites them opaquely. */
export class OceanCaptures {
  private reflection!: MirrorTexture;
  private refraction!: RenderTargetTexture;
  private camera: Camera | null = null;
  private pendingCamera: Camera | null = null;
  private pendingFrame = -1;
  private underwater = false;
  private capturing = false;
  private disposed = false;
  private resolutionScale = 1;
  private enabled = true;
  private seaLevel = 0;
  private readonly inverseRefraction = Matrix.Identity();
  private readonly inverseReflection = Matrix.Identity();
  private environment: BaseTexture | null = null;
  private readonly beforeCamera;
  private readonly afterTargets;
  private readonly resized;
  private readonly savedView = Matrix.Identity();
  private readonly savedProjection = Matrix.Identity();
  private readonly reflectionViewProjection = Matrix.Identity();
  private readonly refractionViewProjection = Matrix.Identity();
  private readonly refractionPlane = new Plane(0, 1, 0, 0);
  private readonly texel = new Vector2();
  private readonly depthNdc = new Vector2();
  private readonly clearRefraction = new Color4(0.012, 0.075, 0.105, 1);
  private readonly restored;
  private readonly sceneDisposed;
  private readonly reflectionList: CaptureRenderList;
  private readonly refractionList: CaptureRenderList;

  constructor(
    private readonly scene: Scene,
    water: AbstractMesh,
    private readonly material: ShaderMaterial,
    private readonly sky: AbstractMesh,
    options: OceanCaptureOptions = {},
  ) {
    const engine = scene.getEngine();
    this.resolutionScale = Math.max(0.25, options.resolutionScale ?? 1);
    this.enabled = options.enabled ?? true;
    this.seaLevel = options.seaLevel ?? 0;
    this.reflectionList = new CaptureRenderList(scene, mesh => mesh !== water, sky);
    this.refractionList = new CaptureRenderList(scene, mesh => mesh !== water && (mesh !== sky || this.underwater), sky);
    this.createTargets();
    this.beforeCamera = scene.onBeforeCameraRenderObservable.add(camera => {
      this.pendingCamera = camera === this.camera && !this.capturing && !this.disposed ? camera : null;
      this.pendingFrame = scene.getFrameId();
    });
    // The same observable also fires before camera rendering. Arm it only for
    // this camera/frame, after which its shadow/depth targets are up to date.
    this.afterTargets = scene.onAfterRenderTargetsRenderObservable.add(() => {
      const camera = this.pendingCamera;
      this.pendingCamera = null;
      if (this.enabled && camera && this.pendingFrame === scene.getFrameId() && scene.activeCamera === camera && !this.disposed) {
        this.capture(camera);
      }
    });
    this.resized = engine.onResizeObservable.add(() => this.resize());
    // The engine rebuilds RTTs; rebind the newly allocated depth attachment too.
    this.restored = engine.onContextRestoredObservable.add(() => {
      if (!this.disposed) {
        this.reflection.dispose();
        this.refraction.dispose();
        this.createTargets();
      }
    });
    this.sceneDisposed = scene.onDisposeObservable.addOnce(() => this.dispose());
  }

  configure(options: OceanCaptureOptions): void {
    const scale = options.resolutionScale === undefined ? this.resolutionScale : Math.max(0.25, options.resolutionScale);
    const enabled = options.enabled ?? this.enabled;
    if (options.seaLevel !== undefined) this.seaLevel = options.seaLevel;
    if (scale === this.resolutionScale && enabled === this.enabled) return;
    this.resolutionScale = scale;
    this.enabled = enabled;
    this.resize();
  }

  /** A null camera suspends captures (the water is hidden). */
  update(camera: Camera | null, underwater: boolean): void {
    this.camera = camera;
    this.underwater = underwater;
    const environment = this.scene.environmentTexture;
    if (environment && this.environment !== environment) {
      this.environment = environment;
      this.material.setTexture("environmentSampler", environment);
    }
  }

  private size(): { width: number; height: number } {
    // Disabled captures keep minimal targets so the water's samplers stay complete.
    if (!this.enabled) return { width: 1, height: 1 };
    const engine = this.scene.getEngine();
    const limit = engine.getCaps().maxTextureSize;
    return {
      width: Math.min(limit, Math.max(1, Math.round(engine.getRenderWidth() * this.resolutionScale))),
      height: Math.min(limit, Math.max(1, Math.round(engine.getRenderHeight() * this.resolutionScale))),
    };
  }

  private createTargets(): void {
    const caps = this.scene.getEngine().getCaps();
    const type = caps.textureHalfFloatRender && caps.textureHalfFloatLinearFiltering
      ? Constants.TEXTURETYPE_HALF_FLOAT : Constants.TEXTURETYPE_UNSIGNED_BYTE;
    const size = this.size();
    this.reflection = new MirrorTexture("ocean-scene-reflection", size, this.scene, false, type);
    this.refraction = new RenderTargetTexture("ocean-scene-refraction", size, this.scene, {
      type, generateMipMaps: false, generateDepthBuffer: true, doNotChangeAspectRatio: true,
      samplingMode: Texture.BILINEAR_SAMPLINGMODE, gammaSpace: false,
    });
    for (const target of [this.reflection, this.refraction]) {
      target.wrapU = Texture.CLAMP_ADDRESSMODE;
      target.wrapV = Texture.CLAMP_ADDRESSMODE;
      target.gammaSpace = false;
      target.disableImageProcessing = true;
      target.useCameraPostProcesses = false;
      target.ignoreCameraViewport = true;
      target.forceLayerMaskCheck = true;
      const list = target === this.reflection ? this.reflectionList : this.refractionList;
      target.getCustomRenderList = () => list.select();
      // Manual camera-pass rendering: do not register in scene.customRenderTargets,
      // so neither RTT can recursively trigger this helper or another water capture.
      target.renderParticles = false;
      target.renderSprites = false;
    }
    this.reflection.onBeforeRenderObservable.add(() => {
      this.material.setMatrix("reflectionViewProjection", this.reflectionViewProjection.copyFrom(this.scene.getTransformMatrix()));
      this.reflectionViewProjection.invertToRef(this.inverseReflection);
      this.material.setMatrix("inverseReflectionViewProjection", this.inverseReflection);
    });
    this.material.setTexture("reflectionSampler", this.reflection);
    this.material.setTexture("refractionSampler", this.refraction);
    this.createDepthAttachment();
    this.material.setVector2("captureTexel", this.texel.set(1 / size.width, 1 / size.height));
    this.material.setFloat("captureEnabled", this.enabled ? 1 : 0);
  }

  private createDepthAttachment(): void {
    const engine = this.scene.getEngine();
    const available = !!engine.getCaps().depthTextureExtension;
    for (const [target, sampler] of [[this.refraction, "refractionDepthSampler"], [this.reflection, "reflectionDepthSampler"]] as const) {
      if (available) {
        target.createDepthStencilTexture(0, false, false, 1, Constants.TEXTUREFORMAT_DEPTH24);
        this.material.setInternalTexture(sampler, target.depthStencilTexture!);
      } else {
        // Even an unreachable branch needs a complete sampler on WebGL1.
        this.material.setTexture(sampler, target);
      }
    }
    this.material.setFloat("captureDepthAvailable", available ? 1 : 0);
    this.material.setVector2("captureDepthNdc", this.depthNdc.set(engine.isNDCHalfZRange ? 1 : 2, engine.isNDCHalfZRange ? 0 : -1));
    this.material.setFloat("captureClearDepth", engine.useReverseDepthBuffer ? 0 : 1);
  }

  private resize(): void {
    if (this.disposed) return;
    const engine = this.scene.getEngine();
    if (engine instanceof ThinEngine && engine._gl.isContextLost()) return;
    const size = this.size();
    this.reflection.resize(size);
    this.refraction.resize(size);
    this.createDepthAttachment();
    this.material.setVector2("captureTexel", this.texel.set(1 / size.width, 1 / size.height));
    this.material.setFloat("captureEnabled", this.enabled ? 1 : 0);
  }

  private capture(camera: Camera): void {
    const scene = this.scene;
    const engine = scene.getEngine();
    if (engine instanceof ThinEngine && engine._gl.isContextLost()) return;
    this.capturing = true;
    const clip = scene.clipPlane;
    const view = this.savedView.copyFrom(scene.getViewMatrix());
    const projection = this.savedProjection.copyFrom(scene.getProjectionMatrix());
    const mirrored = scene._mirroredCameraPosition;
    const forced = scene._forcedViewPosition;
    const sceneUBO = scene.getSceneUniformBuffer();
    const previousTarget = engine._currentRenderTarget;
    const previousViewport = engine.currentViewport;
    const previousPass = engine.currentRenderPassId;
    const previousCamera = scene.activeCamera;
    const skyEnabled = this.sky.isEnabled();
    const side = this.underwater ? 1 : -1;
    this.reflection.activeCamera = camera;
    this.refraction.activeCamera = camera;
    // Negative side of a Babylon clip plane survives. Bias keeps small swell
    // intersections continuous rather than cutting out a thin shoreline band.
    this.reflection.mirrorPlane.normal.set(0, side, 0);
    this.reflection.mirrorPlane.d = -side * this.seaLevel;
    this.reflection.clearColor = scene.clearColor;
    this.refraction.clearColor = this.clearRefraction;
    try {
      this.reflection.render(false);
      scene.setTransformMatrix(view, projection);
      this.refractionPlane.normal.set(0, -side, 0);
      this.refractionPlane.d = side * this.seaLevel - 0.25;
      scene.clipPlane = this.refractionPlane;
      this.refractionViewProjection.copyFrom(scene.getTransformMatrix());
      this.material.setMatrix("refractionViewProjection", this.refractionViewProjection);
      this.refractionViewProjection.invertToRef(this.inverseRefraction);
      this.material.setMatrix("inverseRefractionViewProjection", this.inverseRefraction);
      // The main underwater pass hides the dome, but Snell's window must still
      // contain the real above-water sky in the transmission capture.
      if (this.underwater) this.sky.setEnabled(true);
      this.refraction.render(false);
    } finally {
      // Restore even if compilation, a scene observer, or the draw throws.
      scene.clipPlane = clip;
      scene._mirroredCameraPosition = mirrored;
      this.sky.setEnabled(skyEnabled);
      scene._forcedViewPosition = forced;
      scene.setSceneUniformBuffer(sceneUBO);
      scene.setTransformMatrix(view, projection);
      engine.currentRenderPassId = previousPass;
      if (scene.activeCamera !== previousCamera) scene.activeCamera = previousCamera;
      if (previousTarget) engine.bindFramebuffer(previousTarget);
      else engine.restoreDefaultFramebuffer();
      if (previousViewport) engine.setViewport(previousViewport);
      this.capturing = false;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const engine = this.scene.getEngine();
    this.scene.onBeforeCameraRenderObservable.remove(this.beforeCamera);
    this.scene.onAfterRenderTargetsRenderObservable.remove(this.afterTargets);
    this.scene.onDisposeObservable.remove(this.sceneDisposed);
    engine.onResizeObservable.remove(this.resized);
    engine.onContextRestoredObservable.remove(this.restored);
    this.reflection.dispose();
    this.refraction.dispose();
    this.reflectionList.clear();
    this.refractionList.clear();
    this.camera = null;
    this.pendingCamera = null;
  }
}
